import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ArrowLeft, Plus, Search, Upload, Eye, Download, Trash2, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MeetingMinutes({ user }) {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [meetingName, setMeetingName] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingMeetingId, setUploadingMeetingId] = useState(null);
  const [viewPdfData, setViewPdfData] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    fetchMeetings();
    fetchUsers();
  }, []);

  useEffect(() => {
    filterMeetings();
  }, [searchQuery, statusFilter, meetings]);

  const fetchMeetings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/meeting-minutes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeetings(response.data);
      setFilteredMeetings(response.data);
    } catch (error) {
      console.error('Failed to fetch meetings:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // Auto-refresh every 5 seconds when not editing
  const isUserEditing = createDialogOpen || uploadingPdf || deleteDialogOpen || showPdfViewer || isCreating;
  useAutoRefresh(fetchMeetings, 5, isUserEditing);

  const filterMeetings = () => {
    let filtered = meetings;

    // Filter by search query (search in meeting name and date)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(meeting => {
        const meetingName = meeting.meeting_name.toLowerCase();
        const meetingDate = formatDate(meeting.meeting_date).toLowerCase();
        const meetingDateRaw = meeting.meeting_date.toLowerCase();
        
        return meetingName.includes(query) || 
               meetingDate.includes(query) ||
               meetingDateRaw.includes(query);
      });
    }

    // Filter by status
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(meeting => meeting.status === statusFilter);
    }

    setFilteredMeetings(filtered);
  };

  const handleCreateMeeting = async () => {
    if (!meetingName.trim()) {
      toast.error('Please enter meeting name');
      return;
    }

    if (!meetingDate) {
      toast.error('Please select meeting date');
      return;
    }

    if (selectedParticipants.length === 0) {
      toast.error('Please select at least one participant');
      return;
    }

    setIsCreating(true);
    
    try {
      const token = localStorage.getItem('token');
      const data = {
        meeting_name: meetingName,
        meeting_date: meetingDate,
        participants: selectedParticipants
      };

      await axios.post(`${API}/meeting-minutes/create`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      toast.success('Meeting created successfully');
      setCreateDialogOpen(false);
      setMeetingName('');
      setMeetingDate('');
      setSelectedParticipants([]);
      fetchMeetings();
      
    } catch (error) {
      console.error('Failed to create meeting:', error);
      toast.error(error.response?.data?.detail || 'Failed to create meeting');
    } finally {
      setIsCreating(false);
    }
  };

  const handleInlineFileSelect = (meetingId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (!file.type.includes('pdf')) {
          toast.error('Please select a PDF file');
          return;
        }
        handleUploadPdf(meetingId, file);
      }
    };
    input.click();
  };

  const handleUploadPdf = async (meetingId, file) => {
    setUploadingPdf(true);
    setUploadingMeetingId(meetingId);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/meeting-minutes/${meetingId}/upload-pdf`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('PDF uploaded successfully');
      setUploadingMeetingId(null);
      fetchMeetings();
    } catch (error) {
      console.error('Failed to upload PDF:', error);
      toast.error('Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleViewPdf = async (meeting) => {
    if (!meeting.pdf_url) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}${meeting.pdf_url}`);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setViewPdfData({
          url: reader.result,
          title: meeting.meeting_name
        });
        setShowPdfViewer(true);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Failed to load PDF:', error);
      toast.error('Failed to load PDF');
    }
  };

  const handleDownloadPdf = (meeting) => {
    if (meeting.pdf_url) {
      const link = document.createElement('a');
      link.href = `${BACKEND_URL}${meeting.pdf_url}`;
      link.download = `${meeting.meeting_name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDeleteClick = (meeting) => {
    setMeetingToDelete(meeting);
    setDeleteDialogOpen(true);
  };

  const handleDeletePdfClick = (meeting) => {
    setMeetingToDelete(meeting);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!meetingToDelete) return;

    try {
      const token = localStorage.getItem('token');
      
      if (meetingToDelete.pdf_url) {
        // Delete only the PDF, keep the meeting
        await axios.delete(`${API}/meeting-minutes/${meetingToDelete.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('PDF deleted successfully. Meeting status changed to Pending.');
      } else {
        // Delete the entire meeting
        await axios.delete(`${API}/meeting-minutes/${meetingToDelete.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Meeting deleted successfully');
      }

      setDeleteDialogOpen(false);
      setMeetingToDelete(null);
      fetchMeetings();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'Completed') {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-yellow-100 text-yellow-800';
  };

  const formatDate = (dateStr) => {
    return format(new Date(dateStr), 'MMM dd, yyyy');
  };

  const handleParticipantToggle = (userId) => {
    setSelectedParticipants(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const getParticipantNames = (participantIds) => {
    if (!participantIds || participantIds.length === 0) return 'No participants';
    const names = participantIds.map(id => {
      const user = allUsers.find(u => u.id === id);
      return user ? user.name : 'Unknown';
    });
    return names.join(', ');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-black">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/task-management')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Meeting Minutes
                </h1>
                <p className="text-sm text-gray-600 mt-1">Create and manage meeting minutes</p>
              </div>
            </div>
            <Button onClick={() => setCreateDialogOpen(true)} className="bg-black hover:bg-gray-800">
              <Plus className="w-4 h-4 mr-2" />
              Create Meeting
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Search and Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by meeting name or date..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <SearchableSelect
            options={[
              { value: 'ALL', label: 'All Status' },
              { value: 'Pending', label: 'Pending' },
              { value: 'Completed', label: 'Completed' }
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filter by status"
          />
        </div>

        {/* Meetings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredMeetings.map((meeting) => (
            <div key={meeting.id} className="bg-white border-2 border-black p-4 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{meeting.meeting_name}</h3>
                  <p className="text-sm text-gray-600">{formatDate(meeting.meeting_date)}</p>
                  <p className="text-xs text-gray-500 mt-1">{getParticipantNames(meeting.participants)}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-bold rounded ${getStatusBadge(meeting.status)}`}>
                  {meeting.status}
                </span>
              </div>

              <div className="flex gap-2 mt-4">
                {!meeting.pdf_url ? (
                  <>
                    <Button
                      onClick={() => handleInlineFileSelect(meeting.id)}
                      size="sm"
                      className="flex-1 bg-black hover:bg-gray-800"
                      disabled={uploadingPdf && uploadingMeetingId === meeting.id}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      {uploadingPdf && uploadingMeetingId === meeting.id ? 'Uploading...' : 'Upload PDF'}
                    </Button>
                    <Button
                      onClick={() => handleDeleteClick(meeting)}
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => handleViewPdf(meeting)}
                      size="sm"
                      variant="outline"
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      onClick={() => handleDownloadPdf(meeting)}
                      size="sm"
                      variant="outline"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeletePdfClick(meeting)}
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredMeetings.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">
              {meetings.length === 0 ? 'No meetings found' : 'No meetings match your filters'}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {meetings.length === 0 ? 'Create your first meeting minutes' : 'Try adjusting your filters'}
            </p>
          </div>
        )}
      </main>

      {/* Create Meeting Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Meeting Minutes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Meeting Name</label>
              <Input
                type="text"
                placeholder="Enter meeting name"
                value={meetingName}
                onChange={(e) => setMeetingName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Meeting Date</label>
              <Input
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Participants</label>
              <div className="border rounded-md p-2 max-h-48 overflow-y-auto">
                {allUsers.map(user => (
                  <div key={user.id} className="flex items-center gap-2 py-1">
                    <input
                      type="checkbox"
                      checked={selectedParticipants.includes(user.id)}
                      onChange={() => handleParticipantToggle(user.id)}
                      className="w-4 h-4"
                    />
                    <label className="text-sm">{user.name || user.username}</label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {selectedParticipants.length} participant(s) selected
              </p>
            </div>
            <p className="text-sm text-gray-500">After creating the meeting, upload the PDF from the grid.</p>
          </div>
          <DialogFooter>
            <Button 
              type="button"
              variant="outline" 
              onClick={() => setCreateDialogOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleCreateMeeting}
              disabled={isCreating}
              className="bg-black hover:bg-gray-800"
            >
              {isCreating ? 'Creating...' : 'Create Meeting'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Dialog */}
      {showPdfViewer && viewPdfData && (
        <Dialog open={showPdfViewer} onOpenChange={(open) => {
          setShowPdfViewer(open);
          if (!open) setViewPdfData(null);
        }}>
          <DialogContent className="max-w-screen max-h-screen w-screen h-screen p-0 m-0 rounded-none border-0">
            <DialogHeader className="absolute top-0 left-0 right-0 z-10 px-6 py-3 bg-white border-b">
              <div className="flex items-center justify-between">
                <DialogTitle>{viewPdfData.title}</DialogTitle>
                <button 
                  onClick={() => setShowPdfViewer(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </DialogHeader>
            {viewPdfData && (
              <iframe
                src={viewPdfData.url}
                className="w-full h-full border-0"
                title="Meeting Minutes PDF"
                style={{ marginTop: '60px' }}
                type="application/pdf"
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {meetingToDelete?.pdf_url ? 'Delete PDF' : 'Delete Meeting'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 mb-4">
            {meetingToDelete?.pdf_url 
              ? 'Are you sure you want to delete the PDF? The meeting will remain but status will change to Pending.'
              : 'Are you sure you want to delete this meeting? This action cannot be undone.'
            }
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
